import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Paper,
  Typography,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { parseBsdaDates, formatBsdaDate, dateToBasda, getDateRange, getDayCount } from '../utils/dateUtils';

/**
 * 날짜 선택 컴포넌트 (배치 모드 전용)
 */
const DateSelector = ({
  selectedDates = '',
  onDatesChange,
  disabled = false
}) => {
  const [singleDateInput, setSingleDateInput] = useState(new Date().toISOString().slice(0, 10));
  const [dateRangeStart, setDateRangeStart] = useState(new Date().toISOString().slice(0, 10));
  const [dateRangeEnd, setDateRangeEnd] = useState(new Date().toISOString().slice(0, 10));
  const [showDateRangeDialog, setShowDateRangeDialog] = useState(false);

  const dateList = parseBsdaDates(selectedDates);

  // 단일 날짜 추가
  const handleAddSingleDate = () => {
    const formatted = dateToBasda(singleDateInput);

    if (!dateList.includes(formatted)) {
      const newDates = [...dateList, formatted].sort();
      onDatesChange(newDates.join(', '));
    }
  };

  // 특정 날짜 제거
  const handleRemoveDate = (dateToRemove) => {
    const filtered = dateList.filter(d => d !== dateToRemove);
    onDatesChange(filtered.join(', '));
  };

  // 날짜 범위 추가
  const handleAddDateRange = () => {
    const newDatesFromRange = getDateRange(dateRangeStart, dateRangeEnd);
    const combinedDates = [...new Set([...dateList, ...newDatesFromRange])].sort();
    onDatesChange(combinedDates.join(', '));
    setShowDateRangeDialog(false);
  };

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
        기준일시 (BSDA) 선택
      </Typography>

      {/* 단일 날짜 추가 */}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <TextField
          type="date"
          label="날짜 선택"
          value={singleDateInput}
          onChange={(e) => setSingleDateInput(e.target.value)}
          disabled={disabled}
          sx={{ flex: 1 }}
        />
        <Button
          variant="contained"
          onClick={handleAddSingleDate}
          disabled={disabled}
          sx={{ minWidth: 80 }}
        >
          추가
        </Button>
        <Button
          variant="outlined"
          onClick={() => setShowDateRangeDialog(true)}
          disabled={disabled}
          sx={{ minWidth: 120 }}
        >
          날짜 범위 추가
        </Button>
      </Box>

      {/* 선택된 날짜 목록 */}
      {dateList.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            선택된 날짜 ({dateList.length}개)
          </Typography>
          <Paper variant="outlined" sx={{ p: 1.5, maxHeight: 200, overflow: 'auto' }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {dateList.map((date) => (
                <Chip
                  key={date}
                  label={formatBsdaDate(date)}
                  onDelete={() => handleRemoveDate(date)}
                  disabled={disabled}
                  color="primary"
                  variant="outlined"
                />
              ))}
            </Box>
          </Paper>
        </Box>
      )}

      {dateList.length === 0 && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          날짜를 추가해주세요
        </Alert>
      )}

      {/* 날짜 범위 추가 다이얼로그 */}
      <Dialog
        open={showDateRangeDialog}
        onClose={() => setShowDateRangeDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>날짜 범위 추가</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              type="date"
              label="시작일"
              value={dateRangeStart}
              onChange={(e) => setDateRangeStart(e.target.value)}
              fullWidth
            />
            <TextField
              type="date"
              label="종료일"
              value={dateRangeEnd}
              onChange={(e) => setDateRangeEnd(e.target.value)}
              fullWidth
            />
            <Alert severity="info">
              {(() => {
                const days = getDayCount(dateRangeStart, dateRangeEnd);
                if (days === 0) {
                  return '시작일이 종료일보다 늦습니다';
                }
                return `${days}개의 날짜가 추가됩니다`;
              })()}
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDateRangeDialog(false)}>
            취소
          </Button>
          <Button onClick={handleAddDateRange} variant="contained" color="primary">
            추가
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DateSelector;
